/* eslint-disable import/no-extraneous-dependencies */
import React, { useEffect, useRef } from "react";
import classNames from "classnames";
import gsap from "gsap";

import CoinSvg from "@components/icons/coin-svg";

import { numberWithSpaces } from "@helpers/helpers";

type PropsTypeCoins = {
  coins: number;
  currentCoins?: number;
};

function AnimateCoins({ coins, currentCoins = 0 }: PropsTypeCoins) {
  const coinRef = useRef<HTMLSpanElement>(null);
  const leftContainerRef = useRef<HTMLDivElement>(null);
  const rightContainerRef = useRef<HTMLDivElement>(null);
  const parentRef = useRef<HTMLDivElement>(null);
  const fullContainer = useRef<HTMLSpanElement>(null);
  const coinSvgRef = useRef<SVGSVGElement>(null);

  useEffect(() => {
    if (currentCoins <= 0) return;

    const startValue = coins;
    const endValue = coins + currentCoins;
    const animatedValue = { val: startValue };
    const parentWidth = parentRef.current?.offsetWidth || 0;
    const leftWidth = leftContainerRef.current?.offsetWidth || 0;

    if (fullContainer.current) {
      gsap.set(fullContainer.current, { width: leftWidth });
    }

    if (parentRef.current) {
      gsap.set(parentRef.current, { transformOrigin: "center" });
    }

    const tl = gsap.timeline({ delay: 1 });

    tl.to(
      animatedValue,
      {
        duration: 1.4,
        val: endValue,
        ease: "linear",
        onUpdate: () => {
          if (coinRef.current) {
            coinRef.current.textContent = numberWithSpaces(
              Math.floor(animatedValue.val),
            );
          }
        },
      },
      0,
    );

    if (fullContainer.current) {
      tl.to(
        fullContainer.current,
        {
          duration: 1.4,
          width: "100%",
          ease: "power1.out",
          onComplete: () => {
            if (rightContainerRef.current && fullContainer.current) {
              rightContainerRef.current.style.display = "none";
              fullContainer.current.style.display = "none";
            }
          },
        },
        0,
      );
    }

    tl.to(
      parentRef.current,
      {
        duration: 0.3,
        scaleX: 0.85,
        ease: "power1.out",
      },
      1.4,
    );

    if (coinSvgRef.current) {
      tl.to(coinSvgRef.current, {
        duration: 0.6,
        rotateY: 720,
        y: "-50px",
        ease: "power2.out",
      });
      tl.to(coinSvgRef.current, {
        duration: 1,
        rotateY: 720,
        y: 0,
        ease: "bounce.out",
      });
    }

    tl.to(
      parentRef.current,
      {
        duration: 0.3,
        scaleX: 1,
        ease: "bounce.out",
      },
      1.7,
    );
  }, [coins, currentCoins]);

  return (
    <div className='flex justify-center'>
      <div
        ref={parentRef}
        className='dark:bg-green-default relative flex rounded-xxl bg-green will-change-transform'
      >
        <div
          ref={leftContainerRef}
          className={classNames(
            "dark:bg-yellow-dark-light z-10 flex items-center rounded-xxl bg-yellow-light p-1 pr-2 font-bold text-txt-primary",
          )}
        >
          <CoinSvg
            ref={coinSvgRef}
            width={23}
            height={23}
            classNameProps='mr-1'
          />
          <span ref={coinRef}>{numberWithSpaces(coins)}</span>
        </div>
        {currentCoins > 0 && (
          <div
            ref={rightContainerRef}
            className='dark:bg-green-default min-w-[40px] rounded-xxl bg-green p-1 pr-2 font-bold text-white'
          >
            {currentCoins}
          </div>
        )}
        <span
          ref={fullContainer}
          className='absolute left-0 top-0 h-full rounded-xxl bg-yellow-light'
        />
      </div>
    </div>
  );
}

export default AnimateCoins;
